/**
 * ProgressRing — circular percentage indicator. Used on the home dashboard
 * (small ring), the Idea Dashboard donut hero (large), and the Learning Hub
 * donut. SVG-based, animated stroke-dashoffset, respects reduced motion.
 */
import './ProgressRing.css';

interface ProgressRingProps {
  /** Percentage complete, 0–100. */
  percent: number;
  /** Diameter in px. */
  size?: number;
  /** Stroke width in px. */
  stroke?: number;
  /** Optional centre label under the number. */
  label?: string;
  /** Accessible label describing what the ring represents. */
  ariaLabel?: string;
}

export function ProgressRing({
  percent,
  size = 92,
  stroke = 8,
  label,
  ariaLabel = 'Progress',
}: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, percent));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div
      className="itk-ring"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${ariaLabel}: ${Math.round(clamped)} percent`}
    >
      <svg className="itk-ring__svg" viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="itk-ring__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="itk-ring__fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="itk-ring__centre">
        <span className="itk-ring__pct">{Math.round(clamped)}%</span>
        {label && <span className="itk-ring__lbl">{label}</span>}
      </div>
    </div>
  );
}